/**
 * Kraft Custom Dropdown — progressive enhancement for modal selects.
 *
 * Replaces the native <select> UI inside every .kraft-modal with a styled
 * trigger + listbox, while keeping the native select in the DOM as the
 * source of truth. Nothing else in the stack changes:
 *
 *  - modal-form-handler.js still validates the select (novalidate + inline
 *    .kraft-modal-field-error inserted right after the field) — the error
 *    lands inside our wrapper and is flexed below the trigger via CSS; the
 *    .kraft-modal-input-invalid class on the select styles the trigger
 *    through a sibling selector.
 *  - kraft-script.js still prefills/locks location selects by setting
 *    .value / .disabled directly; we re-sync on modal activation, on the
 *    select's `disabled` attribute flipping, and on real change events.
 *  - Form submission still serializes the native select (name/value).
 *
 * Enhancement targets: select.form-select inside .kraft-modal, skipping
 * [multiple], [size>1], and anything already enhanced. Runs at
 * DOMContentLoaded, again on kraft:lazy-modals-mounted (lazy modal
 * templates), and as a safety net on the first pointerdown.
 *
 * Accessibility: combobox/listbox pattern. Focus stays on the trigger;
 * aria-activedescendant tracks the active option. Arrow keys / Home / End
 * navigate, Enter/Space select, Esc closes, printable keys type-ahead.
 */

(function () {
  'use strict';

  var uid = 0;
  var openDropdown = null; // at most one open panel at a time

  /* ------------------------------------------------------------------ */
  /* Enhancement                                                         */
  /* ------------------------------------------------------------------ */

  function enhanceAll(root) {
    (root || document).querySelectorAll('.kraft-modal select.form-select').forEach(function (sel) {
      if (sel.dataset.ddEnhanced) return;
      if (sel.multiple || sel.size > 1) return;
      enhance(sel);
    });
  }

  function enhance(select) {
    select.dataset.ddEnhanced = 'true';
    uid += 1;
    var ddId = 'kraft-dd-' + uid;

    // Wrapper around the native select
    var wrap = document.createElement('div');
    wrap.className = 'kraft-dd';
    select.parentNode.insertBefore(wrap, select);
    wrap.appendChild(select);

    // Native select becomes the hidden source of truth
    select.classList.add('kraft-dd-native');
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');

    // Trigger button
    var trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'kraft-dd-trigger';
    trigger.id = ddId + '-trigger';
    trigger.setAttribute('role', 'combobox');
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', ddId + '-menu');
    trigger.innerHTML =
      '<span class="kraft-dd-label"></span>' +
      '<svg class="kraft-dd-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">' +
      '<path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>';
    wrap.appendChild(trigger);

    // Listbox panel (options rebuilt on every open so dynamic option
    // changes are always reflected)
    var menu = document.createElement('div');
    menu.className = 'kraft-dd-menu';
    menu.id = ddId + '-menu';
    menu.setAttribute('role', 'listbox');
    menu.hidden = true;
    wrap.appendChild(menu);

    var state = {
      id: ddId,
      select: select,
      wrap: wrap,
      trigger: trigger,
      menu: menu,
      activeIndex: -1,
      typeBuffer: '',
      typeTimer: null
    };

    /* ---------------- sync: native select -> custom UI ---------------- */

    function selectedOption() {
      return select.options[select.selectedIndex] || null;
    }

    function isPlaceholderValue() {
      var opt = selectedOption();
      return !opt || opt.value === '';
    }

    function sync() {
      var opt = selectedOption();
      var label = trigger.querySelector('.kraft-dd-label');
      label.textContent = opt ? opt.textContent.trim() : '';
      wrap.classList.toggle('kraft-dd--placeholder', isPlaceholderValue());
      trigger.disabled = select.disabled;
      wrap.classList.toggle('kraft-dd--locked', select.disabled);
      if (select.disabled) close();
    }
    state.sync = sync;

    /* ---------------- panel open / close ---------------- */

    function buildMenu() {
      menu.innerHTML = '';
      var opts = Array.prototype.slice.call(select.options);
      opts.forEach(function (opt, i) {
        var el = document.createElement('div');
        el.className = 'kraft-dd-option';
        el.id = ddId + '-opt-' + i;
        el.setAttribute('role', 'option');
        el.dataset.index = String(i);
        el.textContent = opt.textContent.trim();
        if (opt.disabled) el.classList.add('kraft-dd-option--disabled');
        if (opt.value === '') el.classList.add('kraft-dd-option--placeholder');
        if (i === select.selectedIndex) {
          el.setAttribute('aria-selected', 'true');
          el.classList.add('kraft-dd-option--selected');
        } else {
          el.setAttribute('aria-selected', 'false');
        }
        menu.appendChild(el);
      });
    }

    function setActive(index, scroll) {
      var options = menu.querySelectorAll('.kraft-dd-option');
      if (!options.length) return;
      index = Math.max(0, Math.min(index, options.length - 1));
      options.forEach(function (o) { o.classList.remove('kraft-dd-option--active'); });
      var el = options[index];
      el.classList.add('kraft-dd-option--active');
      trigger.setAttribute('aria-activedescendant', el.id);
      state.activeIndex = index;
      if (scroll !== false) {
        var mTop = menu.scrollTop;
        var mBottom = mTop + menu.clientHeight;
        if (el.offsetTop < mTop) menu.scrollTop = el.offsetTop;
        else if (el.offsetTop + el.offsetHeight > mBottom) {
          menu.scrollTop = el.offsetTop + el.offsetHeight - menu.clientHeight;
        }
      }
    }

    function open() {
      if (!menu.hidden || select.disabled) return;
      if (openDropdown && openDropdown !== state) openDropdown.close();
      buildMenu();
      menu.hidden = false;

      // Flip upward when there is not enough room below in the viewport
      wrap.classList.remove('kraft-dd--up');
      var rect = trigger.getBoundingClientRect();
      var menuH = Math.min(menu.scrollHeight, 260) + 8;
      if (window.innerHeight - rect.bottom < menuH && rect.top > menuH) {
        wrap.classList.add('kraft-dd--up');
      }

      trigger.setAttribute('aria-expanded', 'true');
      wrap.classList.add('kraft-dd--open');
      openDropdown = state;
      var idx = select.selectedIndex >= 0 ? select.selectedIndex : 0;
      setActive(idx);
    }

    function close() {
      if (menu.hidden) return;
      menu.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      trigger.removeAttribute('aria-activedescendant');
      wrap.classList.remove('kraft-dd--open');
      wrap.classList.remove('kraft-dd--up');
      state.activeIndex = -1;
      if (openDropdown === state) openDropdown = null;
    }
    state.close = close;

    function commit(index) {
      var opt = select.options[index];
      if (!opt || opt.disabled) return;
      if (select.selectedIndex !== index) {
        select.selectedIndex = index;
        // Real bubbling events so every existing listener keeps working
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      sync();
      close();
      trigger.focus();
    }

    /* ---------------- pointer interaction ---------------- */

    trigger.addEventListener('click', function () {
      menu.hidden ? open() : close();
    });

    menu.addEventListener('pointerdown', function (e) {
      // Commit on pointerdown so the choice registers before any
      // focus/blur side effects
      var el = e.target.closest('.kraft-dd-option');
      if (!el || el.classList.contains('kraft-dd-option--disabled')) return;
      e.preventDefault();
      commit(parseInt(el.dataset.index, 10));
    });

    menu.addEventListener('mousemove', function (e) {
      var el = e.target.closest('.kraft-dd-option');
      if (!el || el.classList.contains('kraft-dd-option--disabled')) return;
      var idx = parseInt(el.dataset.index, 10);
      if (idx !== state.activeIndex) setActive(idx, false);
    });

    /* ---------------- keyboard interaction ---------------- */

    function nextEnabled(from, dir) {
      var opts = select.options;
      var i = from;
      do {
        i += dir;
        if (i < 0 || i >= opts.length) return from;
      } while (opts[i].disabled);
      return i;
    }

    trigger.addEventListener('keydown', function (e) {
      var isOpen = !menu.hidden;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (!isOpen) return open();
          setActive(nextEnabled(state.activeIndex, 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (!isOpen) return open();
          setActive(nextEnabled(state.activeIndex, -1));
          break;
        case 'Home':
          if (isOpen) { e.preventDefault(); setActive(nextEnabled(-1, 1)); }
          break;
        case 'End':
          if (isOpen) { e.preventDefault(); setActive(nextEnabled(select.options.length, -1)); }
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (!isOpen) return open();
          if (state.activeIndex >= 0) commit(state.activeIndex);
          break;
        case 'Escape':
          if (isOpen) { e.preventDefault(); close(); }
          break;
        case 'Tab':
          if (isOpen) close();
          break;
        default:
          // Single-character type-ahead (matches native select behavior)
          if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
            e.preventDefault();
            clearTimeout(state.typeTimer);
            state.typeBuffer += e.key.toLowerCase();
            state.typeTimer = setTimeout(function () { state.typeBuffer = ''; }, 500);
            var opts = Array.prototype.slice.call(select.options);
            var start = (isOpen ? state.activeIndex : select.selectedIndex) + 1;
            for (var n = 0; n < opts.length; n++) {
              var i = (start + n) % opts.length;
              if (opts[i].disabled) continue;
              if (opts[i].textContent.trim().toLowerCase().indexOf(state.typeBuffer) === 0) {
                if (isOpen) setActive(i);
                else commit(i);
                break;
              }
            }
          }
      }
    });

    /* ---------------- external sync hooks ---------------- */

    // Real change events (ours or anyone else's)
    select.addEventListener('change', sync);

    // kraft-script.js locks/unlocks the location select via .disabled
    new MutationObserver(sync).observe(select, { attributes: true, attributeFilter: ['disabled'] });

    // Form reset (e.g. after successful Formspark submission)
    var form = select.closest('form');
    if (form) form.addEventListener('reset', function () { setTimeout(sync, 0); });

    // Modal activation: kraft-script.js prefills .value directly (no event)
    // right when the modal's class flips to active — re-sync just after.
    var modal = select.closest('.kraft-modal');
    if (modal && !modal.dataset.ddModalObserved) {
      modal.dataset.ddModalObserved = 'true';
      new MutationObserver(function () {
        var active = modal.classList.contains('active');
        var syncModal = function () {
          modal.querySelectorAll('select.form-select[data-dd-enhanced]').forEach(function (s) {
            var st = registry[s.dataset.ddId];
            if (st) { st.sync(); if (!active) st.close(); }
          });
        };
        setTimeout(syncModal, 0);
        if (active) setTimeout(syncModal, 120); // after prefill/lock settles
      }).observe(modal, { attributes: true, attributeFilter: ['class'] });
    }

    select.dataset.ddId = ddId;
    registry[ddId] = state;
    sync();
  }

  var registry = {};

  /* ------------------------------------------------------------------ */
  /* Global listeners                                                    */
  /* ------------------------------------------------------------------ */

  // Close the open panel on any outside interaction
  document.addEventListener('pointerdown', function (e) {
    if (openDropdown && !openDropdown.wrap.contains(e.target)) openDropdown.close();
  }, true);

  // Re-sync after any modal trigger click (covers every prefill path)
  document.addEventListener('click', function (e) {
    if (!e.target.closest || !e.target.closest('[data-modal-trigger]')) return;
    setTimeout(function () {
      Object.keys(registry).forEach(function (k) { registry[k].sync(); });
    }, 150);
  }, true);

  /* ------------------------------------------------------------------ */
  /* Init                                                                */
  /* ------------------------------------------------------------------ */

  function init() { enhanceAll(document); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Lazy-mounted secondary modals (template materialization)
  document.addEventListener('kraft:lazy-modals-mounted', init);

  // Safety net: enhance anything that appeared some other way
  ['pointerdown', 'keydown'].forEach(function (evt) {
    document.addEventListener(evt, function onFirst() {
      document.removeEventListener(evt, onFirst, true);
      requestAnimationFrame(init);
    }, true);
  });
})();
